/**
 * Bond Recipe Generation
 *
 * Extracts format-dependent bond calculations into reusable recipes.
 * Recipes can be used with both static positions and MotionValues.
 */

import { IAtomModel, IBondModel, AtomContainer, Rings } from '@alchemiesolns/atomcontainer';
import { calculateLineAngleTubeAttributes } from '@alchemiesolns/bond-calc-pure';
import { VisibilityConfigDef, VisibilityFlag } from '@alchemiesolns/visibility-config';
import { getFormat } from './utils/formatContext';
import { BondRecipe, BondTubeRecipe, SpecialBondRecipe } from './types/bondRecipe.types';

/**
 * Counts visible bonds connected to an atom.
 *
 * Legacy O(n) path kept only as a fallback — `generateBondRecipes` precomputes
 * an atom-id → count map and passes it through `BondRecipeLookups` to make
 * each per-bond lookup O(1).
 */
const getVisibleBondCount = (atom: IAtomModel, visibleBonds: IBondModel[]): number => {
  return visibleBonds.filter((b) => b.a1 === atom || b.a2 === atom).length;
};

/**
 * Precomputed lookup tables passed through `generateBondRecipe` to turn the
 * per-bond quadratic scans into O(1) Map lookups.
 *
 * Before PIPH-70, `generateBondRecipe` did five linear scans per bond
 * (atomContainer.atoms.findIndex × 2, atomContainer.bonds.findIndex, and
 * visDefs.find × 2). At 460 bonds × 440 atoms that's roughly a million
 * comparisons per render for index lookups alone, contributing the bulk of
 * the 5.5 ms `generateBondRecipes` cost at 110 atoms.
 *
 * The lookups are built once per render in `generateBondRecipes` (which
 * already has O(n) access to atoms, bonds, visDefs, and visibleBonds) and
 * reused across every `generateBondRecipe` call.
 */
export interface BondRecipeLookups {
  /** atom id → index in `atomContainer.atoms` */
  atomIdToIndex: Map<string, number>;
  /** bond id → index in `atomContainer.bonds` */
  bondIdToIndex: Map<string, number>;
  /** atom-container index → visibility config def (sparse — only set for non-Visible atoms) */
  visDefByIndex: Map<number, VisibilityConfigDef>;
  /** atom id → count of visible bonds attached to that atom */
  visibleBondCountByAtomId: Map<string, number>;
}

/**
 * Calculates the offset direction for double bonds in rings
 * Returns +1 if offset should be in the positive perpendicular direction,
 * -1 if it should be in the negative perpendicular direction.
 *
 * The offset should point toward the ring (where other atoms are).
 *
 * Algorithm:
 * 1. Find all rings that contain this bond
 * 2. Find neighboring atoms (not on this bond) that are in those rings
 * 3. Calculate average position of ring neighbors
 * 4. Determine if neighbors are on the positive or negative side of the perpendicular
 * 5. Return direction that points toward neighbors
 */
const calculateRingOffsetDirection = (
  bond: IBondModel,
  visibleBonds: IBondModel[],
  atomContainer: AtomContainer
): number => {
  // Get bond endpoints
  const a1Pos = bond.a1.pos;
  const a2Pos = bond.a2.pos;

  if (!a1Pos || !a2Pos) {
    return 1; // Default if positions missing
  }

  // Get all rings that contain both atoms of this bond
  const bondRings = Rings.getSharedRings(bond.a1, bond.a2, atomContainer);

  if (!bondRings || bondRings.length === 0) {
    return 1; // Default if bond is not in any ring
  }

  // Collect all atoms that are in at least one of the bond's rings
  const ringAtoms = new Set<IAtomModel>();
  for (const ring of bondRings) {
    for (const atom of ring) {
      ringAtoms.add(atom);
    }
  }

  // Get neighboring atoms (not on this bond) that are in the same ring(s)
  const a1Neighbors = visibleBonds
    .filter((b) => b !== bond && (b.a1 === bond.a1 || b.a2 === bond.a1))
    .map((b) => (b.a1 === bond.a1 ? b.a2 : b.a1))
    .filter((atom) => ringAtoms.has(atom));

  const a2Neighbors = visibleBonds
    .filter((b) => b !== bond && (b.a1 === bond.a2 || b.a2 === bond.a2))
    .map((b) => (b.a1 === bond.a2 ? b.a2 : b.a1))
    .filter((atom) => ringAtoms.has(atom));

  const allNeighbors = [...a1Neighbors, ...a2Neighbors];

  if (allNeighbors.length === 0) {
    return 1; // Default if no neighbors
  }

  // Calculate average neighbor position
  let avgX = 0;
  let avgY = 0;
  let validNeighbors = 0;

  for (const neighbor of allNeighbors) {
    if (neighbor.pos) {
      avgX += neighbor.pos.x;
      avgY += neighbor.pos.y;
      validNeighbors++;
    }
  }

  if (validNeighbors === 0) {
    return 1; // Default if no valid positions
  }

  avgX /= validNeighbors;
  avgY /= validNeighbors;

  // Bond midpoint
  const midX = (a1Pos.x + a2Pos.x) / 2;
  const midY = (a1Pos.y + a2Pos.y) / 2;

  // Vector from bond midpoint to average neighbor
  const toNeighborX = avgX - midX;
  const toNeighborY = avgY - midY;

  // Bond direction vector (from a1 to a2)
  const bondDirX = a2Pos.x - a1Pos.x;
  const bondDirY = a2Pos.y - a1Pos.y;

  // Perpendicular vector using cross product with camDir [0, 0, 1]
  // perp = camDir × bondDir
  // For 2D in SVG coordinates (y-down):
  // perp = [0, 0, 1] × [bondDirX, bondDirY, 0]
  //      = [0*0 - 1*bondDirY, 1*bondDirX - 0*0, 0*bondDirY - 0*bondDirX]
  //      = [-bondDirY, bondDirX, 0]
  const perpX = -bondDirY;
  const perpY = bondDirX;

  // Normalize perpendicular (for consistent dot product)
  const perpLen = Math.sqrt(perpX * perpX + perpY * perpY);
  const perpNormX = perpLen > 0 ? perpX / perpLen : 0;
  const perpNormY = perpLen > 0 ? perpY / perpLen : 0;

  // Dot product: positive if neighbors are on perp side, negative otherwise
  const dot = toNeighborX * perpNormX + toNeighborY * perpNormY;

  // When dot is negative, neighbors are on OPPOSITE side of perp vector
  // We want to point TOWARD neighbors (opposite of perp), so use positive offsetDirection
  // When dot is positive, neighbors are on SAME side as perp vector
  // We want to point TOWARD neighbors (same as perp), so use negative offsetDirection
  // Note: In BondCalcPure.ts, positive offsetDirection means offset goes in OPPOSITE direction of perp
  const result = dot > 0 ? -1 : 1;

  return result;
};

/**
 * Generates a recipe for rendering a bond
 * Contains all format-dependent calculations, ready to use with any positions
 *
 * `lookups` is optional for backward compatibility with any external callers.
 * When present, all per-bond lookups use O(1) Map access instead of the
 * legacy O(n) scans — this is the PIPH-70 fast path used by
 * `generateBondRecipes`.
 */
export function generateBondRecipe(
  bond: IBondModel,
  visibleBonds: IBondModel[],
  isInRing: boolean,
  atomContainer: AtomContainer,
  visDefs: VisibilityConfigDef[],
  lookups?: BondRecipeLookups
): BondRecipe {
  const format = getFormat();
  // Extract bond metadata
  const bondId = bond.id!;
  const atom1Id = bond.a1.id!;
  const atom2Id = bond.a2.id!;
  const order = bond.order;
  const element1 = bond.a1.symbol;
  const element2 = bond.a2.symbol;

  // Calculate neighbor counts (subtract 1 because we don't count the current bond).
  // Fast path (PIPH-70): use precomputed counts when available; fallback to the
  // legacy O(n) filter so external callers without lookups still work.
  const atom1NeighborCount = lookups
    ? (lookups.visibleBondCountByAtomId.get(atom1Id) ?? 0) - 1
    : getVisibleBondCount(bond.a1, visibleBonds) - 1;
  const atom2NeighborCount = lookups
    ? (lookups.visibleBondCountByAtomId.get(atom2Id) ?? 0) - 1
    : getVisibleBondCount(bond.a2, visibleBonds) - 1;

  // Look up indexes. Fast path: O(1) Map lookup. Fallback: O(n) scan.
  const atom1Index = lookups
    ? lookups.atomIdToIndex.get(atom1Id) ?? -1
    : atomContainer.atoms.findIndex((a) => a.id === atom1Id);
  const atom2Index = lookups
    ? lookups.atomIdToIndex.get(atom2Id) ?? -1
    : atomContainer.atoms.findIndex((a) => a.id === atom2Id);
  const bondIndex = lookups
    ? lookups.bondIdToIndex.get(bondId) ?? -1
    : atomContainer.bonds.findIndex((b) => b.id === bondId);

  // Check visibility. Fast path: O(1) Map lookup. Fallback: O(n) scan.
  const visDef1 = lookups ? lookups.visDefByIndex.get(atom1Index) : visDefs.find((v) => v.a === atom1Index);
  const visDef2 = lookups ? lookups.visDefByIndex.get(atom2Index) : visDefs.find((v) => v.a === atom2Index);
  const isHidden =
    visDef1?.visibilityFlag === VisibilityFlag.Hidden ||
    visDef2?.visibilityFlag === VisibilityFlag.Hidden;
  const visibility = isHidden ? 'hidden' : 'visible';

  // Calculate offset direction for ring double bonds
  // This determines which side of the bond the offset line should be on
  let offsetDirection = 1; // Default: positive perpendicular direction
  if (isInRing && order === 2) {
    offsetDirection = calculateRingOffsetDirection(bond, visibleBonds, atomContainer);
  }

  // Calculate tube attributes (offsets and lengths for double/triple bonds)
  const tubeAttribs = calculateLineAngleTubeAttributes({
    bondSize: order,
    a1: element1,
    a2: element2,
    isInRing,
    a1Count: atom1NeighborCount,
    a2Count: atom2NeighborCount,
    format,
    a1VisibilityFlag: visDef1?.visibilityFlag,
    a2VisibilityFlag: visDef2?.visibilityFlag,
    offsetDirection,
  });

  // Generate tube recipes
  const tubes: BondTubeRecipe[] = [];
  for (let lineIndex = 1; lineIndex <= order; lineIndex++) {
    let offset = tubeAttribs.oneOffset;
    if (lineIndex === 2) offset = tubeAttribs.twoOffset;
    else if (lineIndex === 3) offset = tubeAttribs.threeOffset;

    let lengthFactor = tubeAttribs.oneLength;
    if (lineIndex === 2) lengthFactor = tubeAttribs.twoLength;
    else if (lineIndex === 3) lengthFactor = tubeAttribs.threeLength;

    tubes.push({
      lineIndex,
      offset,
      lengthFactor,
      centered: tubeAttribs.centered ?? false,
    });
  }

  // Handle special bond types (wedge, dash, squiggle)
  let special: SpecialBondRecipe | undefined;
  if (bond.type === 'w' || bond.type === 'd' || bond.type === 's') {
    const dashWidth = 0.34;
    const dashStroke = 0.34;
    const dashCount = 7;
    const dashSpacing = 1;

    const boxLength = dashStroke + (dashCount - 1) * dashSpacing;
    const boxWidth = dashWidth * dashCount;

    // Determine atom type combination
    const atomType =
      element1 === 'C' && element2 === 'C'
        ? 'CToC'
        : element1 === 'C' && element2 !== 'C'
        ? 'CToNC'
        : element1 !== 'C' && element2 === 'C'
        ? 'NCToC'
        : 'NCToNC';

    const widthConstant = bond.type === 'd' ? 0.8 : bond.type === 'w' ? 0.3 : -0.2;
    const boxX = boxWidth / 2 + widthConstant;
    const boxY = boxLength / 2 + 0.5;

    special = {
      type: bond.type,
      atomType,
      boxWidth,
      boxLength,
      boxX,
      boxY,
    };
  }

  return {
    bondId,
    atom1Id,
    atom2Id,
    order,
    element1,
    element2,
    atom1VisibilityFlag: visDef1?.visibilityFlag,
    atom2VisibilityFlag: visDef2?.visibilityFlag,
    atom1NeighborCount,
    atom2NeighborCount,
    tubes,
    special,
    isInRing,
    atom1Index,
    atom2Index,
    bondIndex,
    visibility,
  };
}

/**
 * Generates recipes for all bonds in a container.
 *
 * PIPH-70: builds the `BondRecipeLookups` once at the top of this function and
 * passes them through every per-bond `generateBondRecipe` call. The lookups
 * eliminate five linear scans per bond — at 460 bonds × 440 atoms that's the
 * difference between ~1M comparisons and ~2K Map lookups per render. Caller
 * APIs are unchanged; the speedup is internal.
 */
export function generateBondRecipes(
  bonds: IBondModel[],
  ringBonds: IBondModel[],
  atomContainer: AtomContainer,
  visDefs: VisibilityConfigDef[]
): BondRecipe[] {
  // Create a Set of ring bond IDs for fast lookup
  // Use ID-based comparison instead of reference equality
  // This fixes the issue where toAtomContainer() creates new bond objects
  const ringBondIds = new Set(ringBonds.map((rb) => rb.id).filter((id): id is string => id !== undefined));

  // Build O(1) lookup tables once. The cost of building these is O(atoms) +
  // O(bonds) + O(visDefs) + O(bonds * 2) for the neighbor count pass — total
  // linear in workspace size. Per-bond lookups inside generateBondRecipe drop
  // from O(n) to O(1).
  const atomIdToIndex = new Map<string, number>();
  for (let i = 0; i < atomContainer.atoms.length; i++) {
    const id = atomContainer.atoms[i].id;
    if (id !== undefined) atomIdToIndex.set(id, i);
  }

  const bondIdToIndex = new Map<string, number>();
  for (let i = 0; i < atomContainer.bonds.length; i++) {
    const id = atomContainer.bonds[i].id;
    if (id !== undefined) bondIdToIndex.set(id, i);
  }

  const visDefByIndex = new Map<number, VisibilityConfigDef>();
  for (const v of visDefs) visDefByIndex.set(v.a, v);

  // Pre-count visible bonds attached to each atom. The legacy
  // `getVisibleBondCount` did this filter for every bond × every endpoint,
  // making it O(bonds²) total. With the precomputed Map it's O(bonds).
  const visibleBondCountByAtomId = new Map<string, number>();
  for (const b of bonds) {
    const a1 = b.a1.id;
    const a2 = b.a2.id;
    if (a1 !== undefined) visibleBondCountByAtomId.set(a1, (visibleBondCountByAtomId.get(a1) ?? 0) + 1);
    if (a2 !== undefined) visibleBondCountByAtomId.set(a2, (visibleBondCountByAtomId.get(a2) ?? 0) + 1);
  }

  const lookups: BondRecipeLookups = {
    atomIdToIndex,
    bondIdToIndex,
    visDefByIndex,
    visibleBondCountByAtomId,
  };

  return bonds.map((bond) => {
    const isInRing = bond.id !== undefined && ringBondIds.has(bond.id);
    return generateBondRecipe(bond, bonds, isInRing, atomContainer, visDefs, lookups);
  });
}
